import { getPayrollSnapshot } from '../../../core/finance/payrollSnapshot';
import useFinancialView from "../../../core/finance/useFinancialView";
import React, { useMemo } from "react";
import {
  buildWorker360Metrics,
  getWorker360ShiftStart,
  worker360ToDate,
} from "../utils/worker360Metrics";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}


function getPayRate(shift) { return getPayrollSnapshot(shift)?.rate ?? null; }
function getGrossPay(shift) { return getPayrollSnapshot(shift)?.gross ?? null; }

function formatMoney(value) {
  if (value == null) return 'Review required';
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value) || 0);
}

function formatDate(value) {
  const date = worker360ToDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function titleCase(value) {
  return String(value || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function MetricCard({ label, value, color = "#0f172a" }) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 20,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Worker360PayrollContent({
  worker,
  shifts = [],
}) {

  const shiftMetrics = useMemo(
  () => buildWorker360Metrics(worker, shifts),
  [worker, shifts]
);

const { completedShifts } = shiftMetrics;

  const payrollSummary = useMemo(() => {
    if (completedShifts.some(shift => !getPayrollSnapshot(shift))) return { totalHours: null, grossEarnings: null, paidEarnings: null, pendingEarnings: null };
    return completedShifts.reduce(
      (summary, shift) => {
        const hours = getPayrollSnapshot(shift)?.hours ?? null;
        const grossPay = getGrossPay(shift, worker);
        const payrollStatus = normalize(
          shift.payrollPaidAt != null ? "paid" : "pending"
        );

        summary.totalHours += hours;
        summary.grossEarnings += grossPay;

        if (
          ["paid", "processed", "completed"].includes(
            payrollStatus
          )
        ) {
          summary.paidEarnings += grossPay;
        } else {
          summary.pendingEarnings += grossPay;
        }

        return summary;
      },
      {
        totalHours: 0,
        grossEarnings: 0,
        paidEarnings: 0,
        pendingEarnings: 0,
      }
    );
  }, [completedShifts, worker]);

  const averagePayRate =
    payrollSummary.totalHours > 0
      ? payrollSummary.grossEarnings /
        payrollSummary.totalHours
      : null;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Payroll Center
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Earnings calculated from the worker’s completed shifts.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="Gross Earnings"
          value={formatMoney(payrollSummary.grossEarnings)}
          color="#166534"
        />

        <MetricCard
          label="Completed Hours"
          value={(payrollSummary.totalHours == null ? 'Review required' : payrollSummary.totalHours.toFixed(4))}
          color="#1d4ed8"
        />

        <MetricCard
          label="Pending Payroll"
          value={formatMoney(payrollSummary.pendingEarnings)}
          color="#b45309"
        />

        <MetricCard
          label="Paid Payroll"
          value={formatMoney(payrollSummary.paidEarnings)}
          color="#166534"
        />

        <MetricCard
          label="Average Pay Rate"
          value={`${formatMoney(averagePayRate)}/hr`}
          color="#7c3aed"
        />

        <MetricCard
          label="Payable Shifts"
          value={completedShifts.length}
        />
      </div>

      {completedShifts.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No completed shifts are available for payroll.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          <div
            style={{
              color: "#0f172a",
              fontSize: 15,
              fontWeight: 950,
            }}
          >
            Earnings History
          </div>

          {completedShifts.map((shift) => {
            const hours = getPayrollSnapshot(shift)?.hours ?? null;
            const payRate = getPayRate(shift, worker);
            const grossPay = getGrossPay(shift, worker);
            const payrollStatus = normalize(
              shift.payrollPaidAt != null ? "paid" : "pending"
            );

            const paid = [
              "paid",
              "processed",
              "completed",
            ].includes(payrollStatus);

            return (
              <div
                key={shift.id || shift.shiftId}
                style={{
                  padding: 13,
                  borderRadius: 14,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 950,
                      }}
                    >
                      {shift.facilityName ||
                        shift.facilityId ||
                        "Facility"}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 750,
                      }}
                    >
                      {formatDate(getWorker360ShiftStart(shift))}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {(hours == null ? 'Review required' : hours.toFixed(4))} hours ×{" "}
                      {formatMoney(payRate)}/hr
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 15,
                        fontWeight: 950,
                      }}
                    >
                      {formatMoney(grossPay)}
                    </div>

                    <div
                      style={{
                        display: "inline-block",
                        marginTop: 5,
                        padding: "5px 8px",
                        borderRadius: 999,
                        color: paid ? "#166534" : "#92400e",
                        background: paid
                          ? "#dcfce7"
                          : "#fef3c7",
                        fontSize: 9,
                        fontWeight: 950,
                      }}
                    >
                      {titleCase(payrollStatus)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
const EMPTY_SHIFTS = [];
export default function Worker360Payroll(props) {
  const shiftView = useFinancialView("shifts", props.shifts || EMPTY_SHIFTS);
  const workerRows = useMemo(() => [props.worker], [props.worker]);
  const workerView = useFinancialView("users", workerRows);
  if (shiftView.loading || workerView.loading) return <p>Loading financial records…</p>;
  if (shiftView.error || workerView.error) return <p role="alert">Financial records could not be loaded.</p>;
  if (!shiftView.authorized || !workerView.authorized) return <p>Payroll permission required.</p>;
  return <Worker360PayrollContent worker={workerView.rows[0]} shifts={shiftView.rows} />;
}
